import React from "react";
import { Award, Users, Anchor, Package, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useReveal } from "../hooks/useReveal";
import { aboutImage } from "../data/categories";

const ICONS = [Award, Users, Anchor, Package];

export const ChiSiamo = () => {
  const { t } = useI18n();
  const { ref, visible } = useReveal();

  return (
    <section
      id="chi-siamo"
      ref={ref}
      data-testid="about-section"
      className={`reveal ${visible ? "visible" : ""} bg-white py-24 md:py-32`}
    >
      <div className="pb-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Image column */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={aboutImage}
                alt="Workshop"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#1a3a2a]/10" />
            </div>
            {/* Gold accent box */}
            <div className="absolute -bottom-6 -right-6 hidden md:flex flex-col bg-[#c9a84c] text-[#1a3a2a] p-6 w-48">
              <span className="font-heading font-black text-5xl leading-none">1956</span>
              <span className="mt-2 text-[10px] uppercase tracking-[0.2em] font-semibold">
                {t.hero.since}
              </span>
            </div>
          </div>

          {/* Text column */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <span className="block w-12 h-px bg-[#c9a84c]" />
              <span className="text-overline">{t.about.overline}</span>
            </div>
            <h2 className="font-heading font-bold text-[#1a3a2a] tracking-tight text-3xl md:text-5xl leading-[1.05] uppercase">
              {t.about.title}
            </h2>

            <div className="mt-8 space-y-5 text-[#4d6b5d] text-base md:text-lg leading-relaxed max-w-2xl">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
            </div>

            {/* Products list */}
            <div className="mt-10 border-l-2 border-[#c9a84c] pl-6 max-w-xl">
              <p className="text-overline mb-4">{t.about.products_intro}</p>
              <ul className="space-y-3">
                {t.about.products_list.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`about-product-${i}`}
                    className="flex items-start gap-3 text-[#1a3a2a] font-medium"
                  >
                    <CheckCircle2
                      className="w-5 h-5 text-[#c9a84c] mt-0.5 shrink-0"
                      strokeWidth={1.5}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pillars grid */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1a3a2a]/15">
              {t.about.pillars.map((p, i) => {
                const Icon = ICONS[i] || Award;
                return (
                  <div
                    key={i}
                    data-testid={`about-pillar-${i}`}
                    className="bg-white p-6 hover:bg-[#f8f9f8] transition-colors"
                  >
                    <Icon
                      className="w-8 h-8 text-[#c9a84c] mb-4"
                      strokeWidth={1.25}
                    />
                    <h3 className="font-heading font-bold text-[#1a3a2a] text-lg uppercase tracking-tight mb-1">
                      {p.t}
                    </h3>
                    <p className="text-sm text-[#4d6b5d] leading-relaxed">
                      {p.d}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
